import { createContext, useContext } from "react";

// Connection settings (API key + base) persisted to localStorage so a refresh
// never drops them. apiBase defaults to "" → same-origin, which is correct both
// for `vite dev` (paths are proxied to the backend) and production (the Rust
// server hosts the SPA via ServeDir).
export interface Settings {
  apiKey: string;
  apiBase: string;
  /** Web session token (HS256 JWT). Takes precedence over apiKey when present. */
  jwt: string;
  /** Email of the logged-in session, for display only. */
  email: string;
}

export const SETTINGS_KEYS = {
  apiKey: "humanai_apiKey",
  apiBase: "humanai_apiBase",
  jwt: "humanai_jwt",
  email: "humanai_email",
} as const;

export function loadSettings(): Settings {
  return {
    apiKey: localStorage.getItem(SETTINGS_KEYS.apiKey) ?? "",
    apiBase: localStorage.getItem(SETTINGS_KEYS.apiBase) ?? "",
    jwt: localStorage.getItem(SETTINGS_KEYS.jwt) ?? "",
    email: localStorage.getItem(SETTINGS_KEYS.email) ?? "",
  };
}

export function persistSettings(s: Settings): void {
  localStorage.setItem(SETTINGS_KEYS.apiKey, s.apiKey);
  localStorage.setItem(SETTINGS_KEYS.apiBase, s.apiBase);
  localStorage.setItem(SETTINGS_KEYS.jwt, s.jwt);
  localStorage.setItem(SETTINGS_KEYS.email, s.email);
}

export interface SettingsContextValue {
  settings: Settings;
  setSettings: (next: Settings) => void;
  /** True when ANY credential is present (session JWT or developer API key). */
  hasKey: boolean;
  /** True when authenticated via a Web session (JWT). */
  isSession: boolean;
}

export const SettingsContext = createContext<SettingsContextValue | null>(null);

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within <SettingsProvider>");
  return ctx;
}
